import { Component } from '@theme/component';
import { ThemeEvents, CartErrorEvent } from '@theme/events';

/**
 * @typedef {Object} GiftProductRecipientFormRefs
 * @property {HTMLInputElement} myEmailButton
 * @property {HTMLInputElement} recipientEmailButton
 * @property {HTMLDivElement} recipientFields
 * @property {HTMLInputElement} recipientEmail
 * @property {HTMLInputElement} recipientName
 * @property {HTMLInputElement} recipientAddress
 * @property {HTMLTextAreaElement} recipientMessage
 * @property {HTMLInputElement} recipientSendOn
 * @property {HTMLDivElement} [emailError]
 * @property {HTMLDivElement} [nameError]
 * @property {HTMLDivElement} [addressError]
 * @property {HTMLDivElement} [messageError]
 * @property {HTMLDivElement} [sendOnError]
 * @property {HTMLSpanElement} [characterCount]
 * @property {HTMLDivElement} [liveRegion]
 */

/**
 * @extends {Component<GiftProductRecipientFormRefs>}
 */
class GiftProductRecipientForm extends Component {
  static DeliveryMode = {
    SELF: 'self',
    RECIPIENT: 'recipient_form',
  };

  #currentMode = GiftProductRecipientForm.DeliveryMode.SELF;

  /** @type {(() => void) | null} */
  #updateCharacterCountBound = null;
  /** @type {((event: Event) => void) | null} */
  #displayCartErrorBound = null;
  /** @type {(() => void) | null} */
  #cartAddEventBound = null;

  requiredRefs = [
    'myEmailButton',
    'recipientEmailButton',
    'recipientFields',
    'recipientEmail',
    'recipientName',
    'recipientAddress',
    'recipientMessage',
    'recipientSendOn',
  ];

  get #inputFields() {
    return [
      this.refs.recipientEmail,
      this.refs.recipientName,
      this.refs.recipientAddress,
      this.refs.recipientMessage,
      this.refs.recipientSendOn,
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeForm();

    this.#updateCharacterCountBound = () => this.#updateCharacterCount();
    this.refs.recipientMessage.addEventListener('input', this.#updateCharacterCountBound);

    this.#displayCartErrorBound = this.#displayCartError.bind(this);
    // @ts-ignore - bound callback is non-null here
    document.addEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);

    this.#cartAddEventBound = () => this.#handleCartAdd();
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#updateCharacterCountBound) {
      this.refs.recipientMessage.removeEventListener('input', this.#updateCharacterCountBound);
      this.#updateCharacterCountBound = null;
    }

    if (this.#displayCartErrorBound) {
      document.removeEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);
      this.#displayCartErrorBound = null;
    }

    if (this.#cartAddEventBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);
      this.#cartAddEventBound = null;
    }
  }

  #initializeForm() {
    this.#updateButtonStates(GiftProductRecipientForm.DeliveryMode.SELF);
    this.refs.recipientFields.hidden = true;
    this.#clearRecipientFields();
    this.#disableRecipientFields();
    this.#setDateConstraints();
  }

  toggleRecipientForm(mode, _event) {
    if (!Object.values(GiftProductRecipientForm.DeliveryMode).includes(mode)) {
      throw new Error(`Invalid delivery mode: ${mode}`);
    }

    if (this.#currentMode === mode) return;
    this.#currentMode = mode;
    this.#updateFormState();
  }

  #updateFormState() {
    const isRecipientMode = this.#currentMode === GiftProductRecipientForm.DeliveryMode.RECIPIENT;

    this.#updateButtonStates(this.#currentMode);
    this.refs.recipientFields.hidden = !isRecipientMode;

    if (isRecipientMode) {
      this.#enableRecipientFields();
      this.#updateCharacterCount();
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_visible || 'Recipient form fields are now visible';
      }
      this.refs.recipientEmail.focus();
    } else {
      this.#clearRecipientFields();
      this.#disableRecipientFields();
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_hidden || 'Recipient form fields are now hidden';
      }
    }

    this.dispatchEvent(
      new CustomEvent('recipient:toggle', {
        detail: {
          mode: this.#currentMode,
          recipientFormVisible: isRecipientMode,
        },
        bubbles: true,
      })
    );
  }

  #updateButtonStates(mode) {
    switch (mode) {
      case GiftProductRecipientForm.DeliveryMode.SELF:
        this.refs.myEmailButton.checked = true;
        this.refs.recipientEmailButton.checked = false;
        break;
      case GiftProductRecipientForm.DeliveryMode.RECIPIENT:
        this.refs.myEmailButton.checked = false;
        this.refs.recipientEmailButton.checked = true;
        break;
      default:
        this.refs.myEmailButton.checked = true;
        this.refs.recipientEmailButton.checked = false;
    }
  }

  #clearRecipientFields() {
    for (const field of this.#inputFields) {
      field.value = '';
    }

    this.#updateCharacterCount();
    this.#clearErrorMessages();
  }

  #disableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = true;
      field.removeAttribute('required');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    this.#clearErrorMessages();
  }

  #enableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = false;
    }
    this.refs.recipientEmail.setAttribute('required', 'required');
    this.refs.recipientAddress.setAttribute('required', 'required');
    this.refs.recipientSendOn.setAttribute('required', 'required');
    this.#setDateConstraints();
  }

  #updateCharacterCount() {
    if (!this.refs.characterCount) return;

    const currentLength = this.refs.recipientMessage.value.length;
    const maxLength = this.refs.recipientMessage.maxLength;

    const template = this.refs.characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    this.refs.characterCount.textContent = updatedText;
  }

  #setDateConstraints() {
  const today = new Date();
  const minDate = new Date();
  const maxDate = new Date();

  minDate.setDate(today.getDate() + 3);
  maxDate.setDate(today.getDate() + 90);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  this.refs.recipientSendOn.setAttribute('min', formatDate(minDate));
  this.refs.recipientSendOn.setAttribute('max', formatDate(maxDate));

  this.refs.recipientSendOn.addEventListener('change', () => {
    const selectedDate = new Date(`${this.refs.recipientSendOn.value}T00:00:00`);
    const selectedDay = selectedDate.getDay();

    if (selectedDay === 1 || selectedDay === 2 || selectedDay === 3) {
      this.refs.recipientSendOn.value = '';

      if (this.refs.sendOnError) {
        const errorTextElement = this.refs.sendOnError.querySelector('span');

        if (errorTextElement) {
          errorTextElement.textContent =
            'Mondays, Tuesdays, and Wednesdays are unavailable. Please select another delivery date.';
        }

        this.refs.sendOnError.classList.remove('hidden');
      }
    } else if (this.refs.sendOnError) {
      this.refs.sendOnError.classList.add('hidden');
    }
  });
}

  /**
   * @param {CartErrorEvent} event
   */
  #displayCartError(event) {
    if (event.detail?.data) {
      const { message, errors, description } = event.detail.data;

      if (errors && typeof errors === 'object') {
        this.#displayErrorMessage(message || 'There was an error', errors);
      } else if (message) {
        this.#displayErrorMessage(message, description);
      }
    }
  }

  #displayErrorMessage(title, body) {
    this.#clearErrorMessages();

    if (typeof body === 'object' && body !== null) {
      const fieldMap = {
        email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
        name: { inputRef: 'recipientName', errorRef: 'nameError' },
        address: { inputRef: 'recipientAddress', errorRef: 'addressError' },
        message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
        send_on: { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
      };

      for (const [field, errorMessages] of Object.entries(body)) {
        const fieldConfig = fieldMap[field];
        if (!fieldConfig) continue;

        const { inputRef, errorRef } = fieldConfig;
        const errorContainer = this.refs[errorRef];
        const inputElement = this.refs[inputRef];

        if (errorContainer && errorContainer instanceof HTMLElement) {
          const errorTextElement = errorContainer.querySelector('span');
          if (errorTextElement) {
            const messageText = Array.isArray(errorMessages) ? errorMessages.join(', ') : errorMessages;
            errorTextElement.textContent = `${messageText}.`;
          }

          errorContainer.classList.remove('hidden');
        }

        if (inputElement && inputElement instanceof HTMLElement) {
          inputElement.setAttribute('aria-invalid', 'true');
          const errorId = `GiftProductRecipientForm-${field}-error-${this.dataset.sectionId || 'default'}`;
          inputElement.setAttribute('aria-describedby', errorId);
        }
      }
    }

    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent =
        title || Theme.translations?.recipient_form_error || 'There was an error with the form submission';
    }
  }

  #clearErrorMessages() {
    const errorRefs = ['emailError', 'nameError', 'addressError', 'messageError', 'sendOnError'];

    for (const errorRef of errorRefs) {
      const errorContainer = this.refs[errorRef];
      if (errorContainer && errorContainer instanceof HTMLElement) {
        errorContainer.classList.add('hidden');
        const errorTextElement = errorContainer.querySelector('span');
        if (errorTextElement) {
          errorTextElement.textContent = '';
        }
      }
    }

    for (const field of this.#inputFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = '';
    }
  }

  #handleCartAdd() {
    this.#clearErrorMessages();
  }
}

if (!customElements.get('gift-product-recipient-form')) {
  customElements.define('gift-product-recipient-form', GiftProductRecipientForm);
}
